<template>
    <VPanelRow>
        <VTextarea
            v-model="counter.name"
            class="simple-textarea--h2"
            concurrency-mode
            debounce-mode
            maxlength="255"
            required
        ></VTextarea>
    </VPanelRow>

    <VPanelRow padding separate>
        <VInfoList class="numeric-panel__info-list">
            <VInfoItem center :description="t('numeric-counter.key-description')" :label="t('numeric-counter.key')">
                <VInput
                    v-model="counter.key"
                    class="input-field--primary"
                    maxlength="64"
                    :placeholder="t('default-placeholder', 0)"
                ></VInput>
            </VInfoItem>

            <VInfoItem center :label="t('numeric-counter.units')" required>
                <VSelect
                    v-model="counter.units"
                    class="v-select--primary"
                    :disabled="isCreated"
                    :options="unitSelector.options.value"
                    :placeholder="t('default-placeholder', 10)"
                    taggable
                    @input="counter.units = $event.target.value"
                    @open="unitSelector.prefetch"
                >
                </VSelect>
            </VInfoItem>

            <VInfoItem center :description="t('numeric.tag-name-description')" :label="t('numeric.tag-name')">
                <VSelect
                    class="v-select--primary"
                    :model-value="tagName && tagNameSelector.map ? tagNameSelector.map(tagName) : tagName"
                    :options="tagNameSelector.options.value"
                    :placeholder="t('default-placeholder', 0)"
                    :reduce="tagNameSelector.reduce"
                    taggable
                    @input="tagName = $event.target.value"
                    @open="tagNameSelector.prefetch"
                    @option:selected="
                        (event) => {
                            tagName = event.value ?? event.label;
                            tagValue = null;
                        }
                    "
                >
                </VSelect>
            </VInfoItem>

            <VInfoItem center :description="t('numeric.tag-value-description')" :label="t('numeric.tag-value')">
                <template v-if="tagName === 'kpi-numerics.system.user'">
                    <VUserSelectorNew
                        class="v-select--primary v-select--invisible v-select--no-option-padding v-select--form-user-selector"
                        :model-value="tagValue ? [tagValue] : []"
                        @selected="tagValue = $event"
                    ></VUserSelectorNew>
                </template>

                <template v-else>
                    <VSelect
                        v-model="tagValue"
                        class="v-select--primary"
                        :options="tagValuesSelector.options.value"
                        :placeholder="t('default-placeholder', 0)"
                        taggable
                        @input="tagValue = $event.target.value"
                        @open="tagValuesSelector.prefetch"
                    >
                    </VSelect>
                </template>
            </VInfoItem>

            <small
                class="text text--h7 text--negative"
                v-if="counter.units && units && counter.units.toLowerCase() !== units.toLowerCase()"
            >
                {{ t('numeric-counter.units-not-match-description') }}
            </small>
        </VInfoList>
    </VPanelRow>

    <VPanelRow down end gap padding separate-bottom>
        <VButton class="button--primary button--negative button--vcenter" @click="$emit('close')">
            {{ t('cancel') }}

            <TimesSvg></TimesSvg
        ></VButton>

        <VButton class="button--primary button--green button--vcenter" :disabled="isValid" @click="submit">
            {{ t('apply') }}

            <ArrowLeftSvg class="rotate-180"></ArrowLeftSvg
        ></VButton>
    </VPanelRow>
</template>
<script setup lang="ts">
// Svg
import TimesSvg from '../assets/times.svg';
import ArrowLeftSvg from '../assets/arrow-left.svg';

// Components
import VPanelRow from './VPanelRow.vue';
import VInfoList from './VInfoList.vue';
import VInfoItem from './VInfoItem.vue';
import VTextarea from './VTextarea.vue';
import VInput from './VInput.vue';
import VSelect from './VSelect.vue';
import VButton from './VButton.vue';
import VUserSelectorNew from './VUserSelectorNew.vue';

// Other
import INumericCounter from '@/core/Models/INumericCounter';
import { PropType, computed, defineProps, ref, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useNumericCounterTagNameSelector,
    useNumericCounterTagValueSelector,
    useNumericCounterUnitSelector,
} from '@/mixins/SelectorsApi';
import IQueryNumericCounterOptionsRequest from '@/core/Requests/IQueryNumericCounterUnitsRequest';
import { useNumericCounterForm } from '@/mixins/NumericCounterApi';
import { clone } from 'jsondiffpatch';

const { t } = useI18n();
const emit = defineEmits(['submit', 'close']);
const props = defineProps({
    units: { type: String, default: '' },
    tagName: { type: String as PropType<string | null>, default: null },
    tagValue: { type: String as PropType<string | null>, default: null },
    counter: { type: Object as PropType<INumericCounter>, required: true },
});

let counter = ref(clone(props.counter) as INumericCounter);
let tagName = ref(props.tagName);
let tagValue = ref(props.tagValue);

const { createAsync, patchAsync } = useNumericCounterForm(counter);

let isValid = computed(() => !counter.value.units);
let isCreated = computed(() => counter.value.id !== -1);

let tagValueQuery = computed<IQueryNumericCounterOptionsRequest>(() =>
    tagName.value
        ? {
              whereTagName: [tagName.value],
          }
        : {},
);

const unitSelector = useNumericCounterUnitSelector();
const tagNameSelector = useNumericCounterTagNameSelector(null, [
    'kpi-numerics.system.user',
    'kpi-numerics.system.department',
]);
const tagValuesSelector = useNumericCounterTagValueSelector(tagValueQuery);

async function submit() {
    if (!isCreated.value) {
        const result = await createAsync();
        if (!result) {
            return;
        }
        counter.value = result;
    }

    if (counter.value.key !== props.counter.key || counter.value.name !== props.counter.name) {
        await patchAsync();
    }

    emit('submit', {
        counterId: counter.value.id,
        numericTagName: tagName.value,
        numericTagValue: tagValue.value,
    });
    emit('close');
}
</script>
<style lang="scss">
.numeric-counter-control {
    width: 100%;
}
</style>
